import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  Delete,
  Edit,
  Visibility,
  VisibilityOff,
  VpnKey,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import AiCredentialModal from "../../components/AiCredentialModal";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    overflowX: "auto",
  },
  table: {
    minWidth: 400,
  },
  chip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    background: "#e3f0fb",
    color: "#1565c0",
  },
  apiKeyCell: {
    fontFamily: "monospace",
    fontSize: 12,
    color: "#555",
  },
}));

const OPENAI_MODELS = [
  "gpt-4o-mini",
  "gpt-4o",
  "gpt-4-turbo",
  "gpt-4",
  "gpt-3.5-turbo",
];

const AiCredentials = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-credentials");
      setCredentials(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const handleOpenModal = (credential = null) => {
    setSelectedCredential(credential);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCredential(null);
    setModalOpen(false);
  };

  const handleSaved = () => {
    fetchCredentials();
    handleCloseModal();
  };

  const handleDeleteRequest = (credential) => {
    setCredentialToDelete(credential);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/ai-credentials/${credentialToDelete.id}`);
      toast.success("Credencial removida com sucesso");
      fetchCredentials();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteModalOpen(false);
      setCredentialToDelete(null);
    }
  };

  const toggleKeyVisibility = (id) => {
    setVisibleKeys((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const maskKey = (key) => {
    if (!key) return "";
    return key.slice(0, 7) + "••••••••••••••••" + key.slice(-4);
  };

  return (
    <div className={classes.root}>
      <MainHeader>
        <Title>Credenciais de IA</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
          >
            Nova Credencial
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Typography variant="body2" color="textSecondary">
        Gerencie suas chaves de API de inteligência artificial aqui. Após criar uma credencial,
        você pode selecioná-la nos blocos de IA dos fluxos de automação.
      </Typography>

      <Paper className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell>Provedor</TableCell>
              <TableCell>API Key</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credentials.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ color: "#aaa", padding: 32 }}>
                  {loading ? "Carregando..." : "Nenhuma credencial cadastrada ainda."}
                </TableCell>
              </TableRow>
            )}
            {credentials.map((cred) => (
              <TableRow key={cred.id} hover>
                <TableCell>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <VpnKey fontSize="small" style={{ color: "#1565c0" }} />
                    <strong>{cred.name}</strong>
                  </span>
                </TableCell>
                <TableCell>
                  <span className={classes.chip}>{cred.model}</span>
                </TableCell>
                <TableCell style={{ textTransform: "capitalize" }}>{cred.provider}</TableCell>
                <TableCell className={classes.apiKeyCell}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {visibleKeys[cred.id] ? cred.apiKey : maskKey(cred.apiKey)}
                    <IconButton size="small" onClick={() => toggleKeyVisibility(cred.id)}>
                      {visibleKeys[cred.id] ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(cred.createdAt).toLocaleDateString("pt-BR")}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleOpenModal(cred)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRequest(cred)}
                      style={{ color: "#e53935" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <AiCredentialModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSaved={handleSaved}
        credential={selectedCredential}
      />

      <ConfirmationModal
        title="Excluir Credencial"
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Tem certeza que deseja excluir a credencial <strong>{credentialToDelete?.name}</strong>?
        Os fluxos que usam esta credencial precisarão ser reconfigurados.
      </ConfirmationModal>
    </div>
  );
};

export default AiCredentials;
